<template>
  <div class="admin-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model="searchUsername"
              placeholder="搜索用户名"
              prefix-icon="el-icon-search"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            />
            <el-select
              v-model="searchRole"
              placeholder="角色"
              clearable
              class="filter-select-sm"
            >
              <el-option :value="0" label="超级管理员" />
              <el-option :value="1" label="一级代理" />
              <el-option :value="2" label="二级代理" />
            </el-select>
            <el-select
              v-model="searchStatus"
              placeholder="状态"
              clearable
              class="filter-select-sm"
            >
              <el-option :value="0" label="正常" />
              <el-option :value="1" label="禁用" />
            </el-select>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          </div>
          <div class="action-group">
            <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增管理员</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && admins.length === 0"
        description="暂无管理员数据"
      />

      <div v-else>
        <el-table
          :data="admins"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="animate__animated animate__fadeIn"
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" />
          <el-table-column prop="inviteCode" label="邀请码" min-width="120" show-overflow-tooltip />

          <el-table-column prop="role" label="角色" width="120" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.role === 0" type="danger" size="small">超级管理员</el-tag>
              <el-tag v-else-if="scope.row.role === 1" type="warning" size="small">一级代理</el-tag>
              <el-tag v-else-if="scope.row.role === 2" size="small">二级代理</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small">禁用</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="余额" width="120" align="center">
            <template slot-scope="scope">
              <span class="balance-val">{{ formatBalance(scope.row.totalBalance) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="260" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                @click="handleEdit(scope.row)"
              >编辑</el-button>
              <el-button
                size="mini"
                :type="scope.row.status === 0 ? 'warning' : 'success'"
                @click="handleToggleStatus(scope.row)"
              >
                {{ scope.row.status === 0 ? '禁用' : '启用' }}
              </el-button>
              <el-button
                v-if="!isCurrentUser(scope.row)"
                size="mini"
                type="danger"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      append-to-body
      @close="handleDialogClose"
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" :disabled="isEdit" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item v-if="!isEdit" label="密码" prop="password">
          <el-input v-model="form.password" type="password" placeholder="请输入密码" show-password />
        </el-form-item>
        <el-form-item label="邀请码" prop="inviteCode">
          <el-input v-model="form.inviteCode" placeholder="请输入邀请码" maxlength="32" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isEdit && form.role !== 0" label="余额" prop="totalBalance">
          <el-input-number v-model="form.totalBalance" :min="0" :step="1" placeholder="余额" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="form.role" placeholder="请选择角色" style="width: 100%;">
            <el-option :value="0" label="超级管理员" />
            <el-option :value="1" label="一级代理" />
            <el-option :value="2" label="二级代理" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="0">正常</el-radio>
            <el-radio :label="1">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, post, put, del, getResponseMessage } from '@/utils/http'

export default {
  name: 'AdminManagement',
  data() {
    return {
      admins: [],
      loading: false,
      error: '',
      searchAdminId: '',
      searchUsername: '',
      searchRole: null,
      searchStatus: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增管理员',
      isEdit: false,
      form: {
        id: null,
        username: '',
        password: '',
        inviteCode: '',
        role: 1,
        status: 0,
        totalBalance: 0
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在6到20个字符', trigger: 'blur' }
        ],
        inviteCode: [
          { required: true, message: '请输入邀请码', trigger: 'blur' }
        ],
        role: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    currentUserId() {
      // 优先从 localStorage 读取
      const stored = localStorage.getItem('userId')
      if (stored) {
        const parsed = parseInt(stored, 10)
        if (!isNaN(parsed)) return parsed
      }
      // 兜底：从 JWT token 解析
      try {
        const token = localStorage.getItem('token')
        if (!token) return null
        const parts = token.split('.')
        if (parts.length !== 3) return null
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const json = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        const payload = JSON.parse(json)
        const id = payload.adminid || payload.adminId || payload.id || payload.userId || payload.sub || null
        return id ? parseInt(id, 10) || id : null
      } catch {
        return null
      }
    },
    /** 当前登录用户名（用于兜底匹配） */
    currentUsername() {
      return localStorage.getItem('username') || ''
    }
  },
  mounted() {
    this.fetchAdmins()
  },
  methods: {
    async fetchAdmins() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchAdminId) {
          params.adminId = this.searchAdminId
        }
        if (this.searchUsername) {
          params.username = this.searchUsername
        }
        if (this.searchRole !== null && this.searchRole !== '') {
          params.role = this.searchRole
        }
        if (this.searchStatus !== null && this.searchStatus !== '') {
          params.status = this.searchStatus
        }
        const result = await get('/admin/page', params)
        const data = result.data || {}
        this.admins = Array.isArray(data.records) ? data.records : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取管理员列表失败'
        this.admins = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchAdmins()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchAdmins()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchAdmins()
    },

    handleAdd() {
      this.dialogTitle = '新增管理员'
      this.isEdit = false
      this.form = {
        id: null,
        username: '',
        password: '',
        inviteCode: '',
        role: 1,
        status: 0,
        totalBalance: 0
      }
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.dialogTitle = '编辑管理员'
      this.isEdit = true
      this.form = {
        id: row.id,
        username: row.username,
        password: '',
        inviteCode: row.inviteCode || '',
        role: row.role,
        status: row.status,
        totalBalance: row.totalBalance || 0
      }
      this.dialogVisible = true
    },

    handleDialogClose() {
      this.$refs.form && this.$refs.form.resetFields()
    },

    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return

        try {
          if (this.isEdit) {
            const payload = {
              role: this.form.role,
              status: this.form.status,
              inviteCode: this.form.inviteCode
            }
            // 非超管才允许修改余额
            if (this.form.role !== 0) {
              payload.totalBalance = this.form.totalBalance
            }
            const result = await put(`/admin/${this.form.id}`, payload)
            this.dialogVisible = false
            this.$message.success(getResponseMessage(result, '编辑成功'))
          } else {
            const result = await post('/admin/create', {
              username: this.form.username,
              password: this.form.password,
              inviteCode: this.form.inviteCode,
              role: this.form.role
            })
            this.dialogVisible = false
            this.$message.success(getResponseMessage(result, '添加成功'))
          }
          this.fetchAdmins()
        } catch (e) {
          this.$message.error(e.message || '操作失败')
        }
      })
    },

    /** 判断是否是当前登录用户，用于隐藏自己的删除按钮 */
    isCurrentUser(row) {
      // 1. 按 ID 匹配
      if (this.currentUserId != null && String(row.id) === String(this.currentUserId)) {
        return true
      }
      // 2. 按用户名兜底匹配
      if (this.currentUsername && row.username === this.currentUsername) {
        return true
      }
      return false
    },

    formatBalance(val) {
      if (val == null) return '-'
      const v = Number(val) / 1000
      if (v >= 10000) return (v / 10000).toFixed(2) + '万'
      return v.toFixed(2)
    },

    async handleToggleStatus(row) {
      const actionText = row.status === 0 ? '禁用' : '启用'
      try {
        const result = await put(`/admin/${row.id}`, {
          status: row.status === 0 ? 1 : 0
        })
        this.$message.success(getResponseMessage(result, `${actionText}成功`))
        this.fetchAdmins()
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },

    handleDelete(row) {
      this.$confirm(`确定要删除管理员「${row.username}」吗？此操作不可恢复。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const result = await del(`/admin/${row.id}`)
          this.$message.success(getResponseMessage(result, '删除成功'))
          if (this.admins.length === 1 && this.pagination.page > 1) {
            this.pagination.page -= 1
          }
          this.fetchAdmins()
        } catch (e) {
          this.$message.error(e.message || '删除失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 20px;
}

.text-muted {
  color: #c0c4cc;
}

.balance-val {
  font-weight: 600;
  color: #e6a23c;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.el-card.animate__animated,
.el-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
